import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiClient } from './api';
import { stripHtml } from '../lib/app-name-html';

export interface BrandingData {
  appName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  fontFamily: string | null;
}

export interface BrandingContextValue extends BrandingData {
  isLoading: boolean;
  refresh: () => void;
}

const DEFAULTS: BrandingData = {
  appName: 'Blueprint',
  logoUrl: '/logo.svg',
  faviconUrl: null,
  primaryColor: '#3B6B8A',
  accentColor: '#D4A843',
  fontFamily: 'Inter',
};

const BrandingContext = createContext<BrandingContextValue>({
  ...DEFAULTS,
  isLoading: true,
  refresh: () => {},
});

function applyBrandingToDOM(branding: BrandingData) {
  // Color/font CSS variables are owned by ThemeProvider (theme-context.tsx); branding
  // only manages the document title and favicon now.
  document.title = stripHtml(branding.appName) || 'Blueprint';
  if (branding.faviconUrl) {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = branding.faviconUrl;
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingData>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBranding = useCallback(() => {
    apiClient
      .get<{ data: Partial<BrandingData> }>('/public/branding')
      .then((res) => {
        // A custom uploaded logo wins; absence (null) falls back to the bundled
        // default SVG rather than the generic icon.
        const merged = {
          ...DEFAULTS,
          ...res.data,
          logoUrl: res.data.logoUrl ?? DEFAULTS.logoUrl,
        };
        setBranding(merged);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    applyBrandingToDOM(branding);
  }, [branding]);

  const value: BrandingContextValue = { ...branding, isLoading, refresh: fetchBranding };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
